import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { useAppContext } from "../../AppContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import orderRoute from "../../services/orderRoute";
import arrowLeft from "../../assets/arrow-left.svg";
import CancelScheduledUpdateModal from "../../components/Modal/CancelScheduledUpdateModal";

const AddLocation = () => {
  const { showToast } = useAppContext();

  const navigate = useNavigate();
  const { lgaId } = useParams();
  const { getDeliveryStates, getLocation, addLocation, editLocation, revertScheduledUpdate } =
    orderRoute();
  const { state } = useLocation();
  const [isEditing, setIsEditing] = useState(state?.isEditing || false);
  const [loading, setLoading] = useState(false);
  const [deliveryStates, setDeliveryStates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cancelTrail, setCancelTrail] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    stateId: "",
    deliveryFee: "",
    deliveryDays: [],
    trails: [],
  });

  const [errors, setErrors] = useState({});

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const getDaySuffix = (day) => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatTrailDate = (dateString) => {
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();
    return `${dayName}, ${day}${getDaySuffix(day)} of ${month}, ${year}`;
  };

  const formatRangeDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();
    return `${day}${getDaySuffix(day)} of ${month}, ${year}`;
  };

  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

  useEffect(() => {
    getDeliveryStates(showToast, setDeliveryStates);
  }, []);

  useEffect(() => {
    if (lgaId) {
      setLoading(true);
      getLocation(lgaId, setLoading, showToast, setFormData);
    }
  }, [lgaId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };
  const handleEdit = () => {
    setIsEditing(true);
  };
  const handleDayToggle = (day) => {
    const lowerDay = day.toLowerCase();
    setFormData((prev) => {
      const days = prev.deliveryDays || [];
      if (days.includes(lowerDay)) {
        return { ...prev, deliveryDays: days.filter((d) => d !== lowerDay) };
      } else {
        return { ...prev, deliveryDays: [...days, lowerDay] };
      }
    });

    if (errors.deliveryDays) {
      setErrors((prev) => ({ ...prev, deliveryDays: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Delivery area name is required";
    }

    if (!formData.stateId) {
      newErrors.stateId = "State is required";
    }

    if (!formData.deliveryFee || formData.deliveryFee <= 0) {
      newErrors.deliveryFee = "Delivery fee must be greater than 0";
    }

    if (!formData.deliveryDays || formData.deliveryDays.length === 0) {
      newErrors.deliveryDays = "Please select at least one delivery day";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirmCancel = () => {
    if (!cancelTrail) return;
    const body = { overrideTrailId: cancelTrail.id };
    revertScheduledUpdate(body, setCancelLoading, showToast, () => {
      setCancelTrail(null);
      getLocation(lgaId, setLoading, showToast, setFormData);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast({ toastText: "Please fill in all required fields", isSuccess: false });
      return;
    }

    setIsSubmitting(true);
    const orderedDays = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    const body = {
      name: formData.name.trim(),
      state: formData.stateId,
      deliveryFee: parseFloat(formData.deliveryFee),
      deliveryDays: formData.deliveryDays
        .map((day) => day.toLowerCase())
        .sort((a, b) => orderedDays.indexOf(a) - orderedDays.indexOf(b)),
    };

    if (lgaId) {
      // Update existing location — stay on details page and refetch
      editLocation(lgaId, body, setIsSubmitting, showToast, () => {
        setIsEditing(false);
        getLocation(lgaId, setLoading, showToast, setFormData);
      });
    } else {
      // Create new location
      addLocation(body, setLoading, showToast);
    }
  };

  return (
    <section>
      <div className="d-flex gap-3 align-items-center header-admin  py-4 pe-5 mb-4 ps-4">
        <img src={arrowLeft} onClick={() => navigate(-1)} alt="arrow" />
        <h3 style={{ color: "#344054" }}>{lgaId ? " Location Details" : "Add New Location"}</h3>
        {lgaId && !isEditing && (
          <p
            style={{ cursor: "pointer", textDecoration: "underline", color: "#469B12" }}
            onClick={handleEdit}
            className="my-3"
          >
            Edit
          </p>
        )}
      </div>

      <div className="pe-5 ps-4">
        {loading ? (
          <div
            className="justify-content-center align-items-center text-center"
            style={{ padding: "100px" }}
          >
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <div className="">
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Delivery Area <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      placeholder="Enter delivery area name"
                      value={formData.name}
                      onChange={handleInputChange}
                      isInvalid={!!errors.name}
                      disabled={isSubmitting || (lgaId && !isEditing)}
                    />
                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      State <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="stateId"
                      value={formData.stateId}
                      onChange={handleInputChange}
                      isInvalid={!!errors.stateId}
                      disabled={isSubmitting || (lgaId && !isEditing)}
                    >
                      <option value="">Select a state</option>
                      {deliveryStates.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.stateId}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Delivery Fee (₦) <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="deliveryFee"
                      placeholder="Enter delivery fee"
                      value={formData.deliveryFee}
                      onChange={(event) => {
                        let value = event.target.value;

                        // Allow only digits and a single decimal point
                        if (/^[0-9]*\.?[0-9]*$/.test(value)) {
                          setFormData({ ...formData, deliveryFee: value });
                        }
                      }}
                      isInvalid={!!errors.deliveryFee}
                      disabled={isSubmitting || (lgaId && !isEditing)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.deliveryFee}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Delivery Day(s) <span className="text-danger">*</span>
                    </Form.Label>
                    <div
                      className={`p-3 border rounded ${errors.deliveryDays ? "border-danger" : ""}`}
                      style={{ backgroundColor: "#f8f9fa" }}
                    >
                      <Row>
                        {daysOfWeek.map((day) => (
                          <Col md={6} key={day} className="mb-2">
                            <Form.Check
                              type="checkbox"
                              id={`day-${day}`}
                              label={day}
                              checked={formData.deliveryDays?.includes(day.toLowerCase())}
                              onChange={() => handleDayToggle(day)}
                              disabled={isSubmitting || (lgaId && !isEditing)}
                            />
                          </Col>
                        ))}
                      </Row>
                    </div>
                    {errors.deliveryDays && (
                      <div className="text-danger small mt-1">{errors.deliveryDays}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              {!isEditing && lgaId && formData.trails && formData.trails.length > 0 && (
                <Row className="mb-4">
                  <Col>
                    <Form.Label className="fw-semibold">Activity History</Form.Label>
                    <div className="p-3 border rounded" style={{ backgroundColor: "#f8f9fa" }}>
                      <ul className="mb-0 ps-4">
                        {(() => {
                          const trailKey = (t) =>
                            `${(t.days || [])
                              .slice()
                              .sort()
                              .join(",")}|${t.startDate || ""}|${t.endDate || ""}|${t.action || ""}`;
                          const revertedKeys = new Set(
                            formData.trails
                              .filter((t) => t.updateType === "reverted_delivery_days")
                              .map(trailKey)
                          );
                          return formData.trails.map((trail, index) => {
                            const wasReverted =
                              trail.updateType === "delivery_days" &&
                              revertedKeys.has(trailKey(trail));
                            const days = (trail.days || []).map(capitalize);
                          const dayLabel =
                            days.length === 0
                              ? ""
                              : days.length === 1
                                ? days[0]
                                : days.length === 2
                                  ? `${days[0]} and ${days[1]}`
                                  : `${days.slice(0, -1).join(", ")}, and ${days[days.length - 1]}`;
                          const updatedByName = [
                            trail.updatedBy?.firstName,
                            trail.updatedBy?.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ");
                          const hasFutureStart =
                            trail.startDate &&
                            trail.createdAt &&
                            new Date(trail.startDate).getTime() -
                              new Date(trail.createdAt).getTime() >
                              60000;
                          const isScheduled = hasFutureStart || !!trail.endDate;
                          const isReverted = trail.updateType === "reverted_delivery_days";
                          const isActionTrail =
                            trail.action === "activate" || trail.action === "deactivate";
                          const startInFuture =
                            trail.startDate &&
                            new Date(trail.startDate).getTime() > Date.now();
                          const canCancel =
                            isActionTrail && startInFuture && !isReverted && !wasReverted;

                          let rangeText = "";
                          if (hasFutureStart && trail.endDate) {
                            rangeText = ` (From ${formatRangeDate(trail.startDate)} to ${formatRangeDate(trail.endDate)})`;
                          } else if (hasFutureStart) {
                            rangeText = ` (From ${formatRangeDate(trail.startDate)})`;
                          } else if (trail.endDate) {
                            rangeText = ` (Until ${formatRangeDate(trail.endDate)})`;
                          }

                          let body;
                          if (isReverted) {
                            const actionLabel =
                              trail.action === "activate" ? "activation" : "deactivation";
                            let revertRange = "";
                            if (trail.startDate && trail.endDate) {
                              revertRange = ` from ${formatRangeDate(trail.startDate)} to ${formatRangeDate(trail.endDate)}`;
                            } else if (trail.startDate) {
                              revertRange = ` from ${formatRangeDate(trail.startDate)}`;
                            } else if (trail.endDate) {
                              revertRange = ` until ${formatRangeDate(trail.endDate)}`;
                            }
                            body = (
                              <>
                                {dayLabel} {actionLabel}
                                {revertRange} was cancelled by{" "}
                                {updatedByName || trail.updatedBy?.email || "Unknown"} on{" "}
                                {formatTrailDate(trail.createdAt)}
                              </>
                            );
                          } else if (trail.action === "cancel" || trail.action === "revert") {
                            const originalAction =
                              trail.targetAction ||
                              trail.originalAction ||
                              trail.revertedAction;
                            const actionLabel =
                              originalAction === "activate" ? "activation" : "deactivation";
                            body = (
                              <>
                                {updatedByName || trail.updatedBy?.email || "Someone"} cancelled
                                the {actionLabel} of {dayLabel}
                                {rangeText} on {formatTrailDate(trail.createdAt)}
                              </>
                            );
                          } else if (trail.notes) {
                            body = (
                              <>
                                {trail.notes} on {formatTrailDate(trail.createdAt)}
                              </>
                            );
                          } else {
                            const verb = days.length === 1 ? "was" : "were";
                            body = (
                              <>
                                {dayLabel} {verb} {trail.action}d on{" "}
                                {formatTrailDate(trail.createdAt)} by{" "}
                                {updatedByName || trail.updatedBy?.email || "Unknown"}
                                {rangeText}
                              </>
                            );
                          }

                          return (
                            <li key={index} className="mb-2">
                              <div className="d-flex justify-content-between align-items-start gap-3">
                                <span>{body}</span>
                                {canCancel && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => setCancelTrail(trail)}
                                    style={{
                                      color: "#D92D20",
                                      textDecoration: "underline",
                                      padding: 0,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </div>
                            </li>
                          );
                          });
                        })()}
                      </ul>
                    </div>
                  </Col>
                </Row>
              )}
              {!isEditing && lgaId ? null : (
                <Row className="mt-5">
                  <Col className="d-flex gap-3">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting}
                      style={{ minWidth: "120px" }}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          {lgaId ? "Updating..." : "Creating..."}
                        </>
                      ) : (
                        <>Save</>
                      )}
                    </Button>
                  </Col>
                </Row>
              )}
            </div>
          </Form>
        )}
      </div>

      <CancelScheduledUpdateModal
        show={!!cancelTrail}
        onHide={() => setCancelTrail(null)}
        trail={cancelTrail}
        onConfirm={handleConfirmCancel}
        loading={cancelLoading}
      />
    </section>
  );
};

export default AddLocation;
